import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { characterPath, compendiumPath, type BookEntry } from "../config/site";
import { describeChapterList, type KeyItemEntry } from "../data/key-items";
import { loadPortraitUrls } from "../data/load-book-home";
import { BookSpoilers } from "./BookSpoilers";
import { useScrollToHash } from "./useScrollToHash";

interface Row {
  book: BookEntry;
  entry: KeyItemEntry;
  imageUrl: string | undefined;
}

async function loadEntries(book: BookEntry): Promise<Row[]> {
  const response = await fetch(`/book-data/${book.id}/key-items.json`);
  if (!response.ok || !(response.headers.get("content-type") ?? "").includes("json")) {
    return [];
  }
  const { items } = await response.json() as { items: KeyItemEntry[] };
  const urlOf = await loadPortraitUrls(book.id);
  return items.map((entry) => ({ book, entry, imageUrl: urlOf(`item-${entry.id}`) }));
}

/** The key items in these books, who had them, and when. Hidden when there are none. */
export function KeyItemEntries({ books }: { books: readonly BookEntry[] }) {
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    void Promise.all(books.map(loadEntries)).then((all) => setRows(all.flat()));
  }, [books]);

  useScrollToHash("items", rows.length > 0);

  if (rows.length === 0) {
    return null;
  }
  return (
    <section id="items" className="key-items" aria-label="Key items">
      <h2 className="book-section-title">Key items</h2>
      <p className="key-items__intro">Objects that matter to the story, and who had them.</p>
      <ul className="key-items__list">
        {rows.map(({ book, entry, imageUrl }) => (
          <li key={`${book.id}:${entry.id}`} className="key-items__item">
            {imageUrl === undefined ? null : <img className="key-items__image" src={imageUrl} alt="" />}
            <Link to={compendiumPath(book, entry.id, "item")}><strong>{entry.name}</strong></Link>
            {entry.description === undefined ? null : (
              <span className="key-items__about">{entry.description}</span>
            )}
            <ItemHistory book={book} entry={entry} manyBooks={books.length > 1} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** When it appears and who had it, which give away the story, so closed until opened. */
function ItemHistory(
  { book, entry, manyBooks }: { book: BookEntry; entry: KeyItemEntry; manyBooks: boolean },
) {
  const history = (
    <>
    <span className="key-items__when">
      Appears in {manyBooks ? `${book.title}, ` : ""}{describeChapterList(entry.chapterIds)}
    </span>
    {entry.fate === undefined ? null : (
      <span className="key-items__fate">
        {entry.fate.label} in {describeChapterList([entry.fate.chapterId])}
      </span>
    )}
    <span className="key-items__holders">
      Held by{" "}
      {entry.holders.map((holder, index) => (
        <span key={holder.characterId}>
          {index === 0 ? "" : ", "}
          <Link to={characterPath(book, holder.characterId)}>{holder.displayName}</Link>
        </span>
      ))}
    </span>
    </>
  );
  return <BookSpoilers bookId={book.id} bookTitle={book.title}>{history}</BookSpoilers>;
}
